import { createSlice } from "@reduxjs/toolkit";
import {
  addAgriTechRate,
  addBatteryEnergyRate,
  addCommodity,
  editAgriTechRate,
  editBatteryEnergyRate,
  editCommodity,
  getAgriTechRateList,
  getAllAgriTechRates,
  getAllBatteryEnergyRates,
  getAllCommodities,
  getBatteryEnergyRateList,
  getCommodities,
} from "../APIs/ratesAPI";

const initialState = {
  // common
  loading: false,
  backgroundLoading: false,
  error: {},
  status: "idle",
  showToastMessage: {
    visible: false,
    data: {},
  },

  // commodities
  commodityList: {
    result: [],
    totalRecords: 0,
  },
  allCommodities: [],
  currentCommodity: {},
  threeDotsPopoverId: 0,
  deleteCommodityModal: false,

  // battery energy
  allRates: [],
  batteryEnergyRateList: {
    result: [],
    totalRecords: 0,
  },
  currentEnergyRate: {},
  energyThreeDotsPopoverId: 0,
  deleteEnergyRateModal: false,

  // agri tech
  allAgriTechRates: [],
  agriTechRateList: {
    result: [],
    totalRecords: 0,
  },
  currentAgriTechRate: {},
  agriTechThreeDotsPopoverId: 0,
  deleteAgriTechModal: false,
};

export const ratesSlice = createSlice({
  name: "rates",
  initialState,
  reducers: {
    updateRates: (state, action) => {
      state[action.payload.key] = action.payload.value;
    },
  },
  extraReducers(builder) {
    // add commodity
    builder.addCase(addCommodity.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(addCommodity.fulfilled, (state) => {
      state.loading = false;
      state.status = "fulfilled";
    });
    builder.addCase(addCommodity.rejected, (state, action) => {
      state.loading = false;
      state.status = "rejected";
      state.error = action.error;
    });

    // get commodities
    builder.addCase(getCommodities.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getCommodities.fulfilled, (state, action) => {
      state.loading = false;
      state.status = "fulfilled";
      state.commodityList = action.payload;
    });
    builder.addCase(getCommodities.rejected, (state, action) => {
      state.loading = false;
      state.status = "rejected";
      state.error = action.error;
    });

    // edit commodity
    builder.addCase(editCommodity.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(editCommodity.fulfilled, (state) => {
      state.loading = false;
      state.status = "fulfilled";
    });
    builder.addCase(editCommodity.rejected, (state, action) => {
      state.loading = false;
      state.status = "rejected";
      state.error = action.error;
    });

    // get all commodities
    builder.addCase(getAllCommodities.pending, (state) => {
      state.backgroundLoading = true;
    });
    builder.addCase(getAllCommodities.fulfilled, (state, action) => {
      state.backgroundLoading = false;
      state.status = "fulfilled";
      state.allCommodities = action.payload.result;
    });
    builder.addCase(getAllCommodities.rejected, (state, action) => {
      state.backgroundLoading = false;
      state.status = "rejected";
      state.error = action.error;
    });

    // battery energy
    // add battery energy rate
    builder.addCase(addBatteryEnergyRate.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(addBatteryEnergyRate.fulfilled, (state) => {
      state.loading = false;
      state.status = "fulfilled";
    });
    builder.addCase(addBatteryEnergyRate.rejected, (state, action) => {
      state.loading = false;
      state.status = "rejected";
      state.error = action.error;
    });

    // get battery energy rates
    builder.addCase(getBatteryEnergyRateList.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getBatteryEnergyRateList.fulfilled, (state, action) => {
      state.loading = false;
      state.status = "fulfilled";
      state.batteryEnergyRateList = action.payload;
    });
    builder.addCase(getBatteryEnergyRateList.rejected, (state, action) => {
      state.loading = false;
      state.status = "rejected";
      state.error = action.error;
    });

    // edit battery energy rate
    builder.addCase(editBatteryEnergyRate.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(editBatteryEnergyRate.fulfilled, (state) => {
      state.loading = false;
      state.status = "fulfilled";
    });
    builder.addCase(editBatteryEnergyRate.rejected, (state, action) => {
      state.loading = false;
      state.status = "rejected";
      state.error = action.error;
    });

    // get all battery energy rates
    builder.addCase(getAllBatteryEnergyRates.pending, (state) => {
      state.backgroundLoading = true;
    });
    builder.addCase(getAllBatteryEnergyRates.fulfilled, (state, action) => {
      state.backgroundLoading = false;
      state.status = "fulfilled";
      state.allRates = action.payload.result;
    });
    builder.addCase(getAllBatteryEnergyRates.rejected, (state, action) => {
      state.backgroundLoading = false;
      state.status = "rejected";
      state.error = action.error;
    });

    // agri tech
    // add agri tech rate
    builder.addCase(addAgriTechRate.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(addAgriTechRate.fulfilled, (state) => {
      state.loading = false;
      state.status = "fulfilled";
    });
    builder.addCase(addAgriTechRate.rejected, (state, action) => {
      state.loading = false;
      state.status = "rejected";
      state.error = action.error;
    });

    // get agri tech rates
    builder.addCase(getAgriTechRateList.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getAgriTechRateList.fulfilled, (state, action) => {
      state.loading = false;
      state.status = "fulfilled";
      state.agriTechRateList = action.payload;
    });
    builder.addCase(getAgriTechRateList.rejected, (state, action) => {
      state.loading = false;
      state.status = "rejected";
      state.error = action.error;
    });

    // edit agri tech rate
    builder.addCase(editAgriTechRate.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(editAgriTechRate.fulfilled, (state) => {
      state.loading = false;
      state.status = "fulfilled";
    });
    builder.addCase(editAgriTechRate.rejected, (state, action) => {
      state.loading = false;
      state.status = "rejected";
      state.error = action.error;
    });

    // get all agri tech rates
    builder.addCase(getAllAgriTechRates.pending, (state) => {
      state.backgroundLoading = true;
    });
    builder.addCase(getAllAgriTechRates.fulfilled, (state, action) => {
      state.backgroundLoading = false;
      state.status = "fulfilled";
      state.allAgriTechRates = action.payload.result;
    });
    builder.addCase(getAllAgriTechRates.rejected, (state, action) => {
      state.backgroundLoading = false;
      state.status = "rejected";
      state.error = action.error;
    });
  },
});

export const { updateRates } = ratesSlice.actions;

export default ratesSlice.reducer;
